/* Keep mobile navigation available only when the header cannot serve it. */
if (!customElements.get('kp-mobile-navigation')) {
  customElements.define('kp-mobile-navigation', class extends HTMLElement {
    connectedCallback() {
      this.mobileQuery = window.matchMedia('(max-width: 767px)');
      this.header = document.querySelector('.m-header__mobile') || document.querySelector('.m-header__wrapper');
      this.lastHeight = null;
      this.headerTransition = this.header?.closest('.m-header__wrapper') || this.header;
      this.schedule = () => {
        if (this.frame != null) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.setVisible(this.shouldShow());
        });
      };
      this.headerTransition?.addEventListener('transitionend', this.schedule);
      this.mobileQuery.addEventListener('change', this.schedule);
      window.addEventListener('resize', this.schedule);
      window.addEventListener('pageshow', this.schedule);
      window.visualViewport?.addEventListener('resize', this.schedule);
      window.visualViewport?.addEventListener('scroll', this.schedule);
      document.addEventListener('focusin', this.schedule);
      document.addEventListener('focusout', this.schedule);
      // Observe the moving header itself: a document-position sentinel misses
      // the sticky header returning when the shopper scrolls upwards.
      if (this.header && 'IntersectionObserver' in window) {
        this.headerObserver = new IntersectionObserver(this.schedule, { threshold: [0, 1] });
        this.headerObserver.observe(this.header);
      } else {
        window.addEventListener('scroll', this.schedule, { passive: true });
      }
      this.stateObserver = new MutationObserver(this.schedule);
      this.stateObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
      this.stateObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
      this.dialogObserver = new MutationObserver(this.schedule);
      this.dialogObserver.observe(document.body, { subtree: true, attributes: true, attributeFilter: ['open'] });
      if ('ResizeObserver' in window) {
        this.sizeObserver = new ResizeObserver(this.schedule);
        this.sizeObserver.observe(this);
        if (this.header) this.sizeObserver.observe(this.header);
      }
      this.schedule();
    }

    shouldShow() {
      if (!this.mobileQuery.matches) return false;
      const root = document.documentElement;
      if (root.classList.contains('stick-atc-show')) return false;
      if ([root, document.body].some(el => el.classList.contains('prevent-scroll') || el.classList.contains('m:overflow-hidden'))) return false;
      if (document.querySelector('dialog[open], .m-cart-drawer--active')) return false;
      const focused = document.activeElement;
      if (focused?.matches('input:not([type="checkbox"]):not([type="radio"]), textarea, select, [contenteditable="true"]')) return false;
      // Do not float controls above an on-screen keyboard after focus changes.
      const viewport = window.visualViewport;
      if (viewport && viewport.scale === 1 && window.innerHeight - viewport.height > 150) return false;
      if (!this.header) return true;
      const rect = this.header.getBoundingClientRect();
      const style = getComputedStyle(this.header);
      const top = viewport?.offsetTop || 0;
      const bottom = top + (viewport?.height || window.innerHeight);
      const headerVisible = style.display !== 'none' && style.visibility !== 'hidden' && rect.width > 0 && rect.height > 0 && rect.bottom > top && rect.top < bottom;
      return !headerVisible;
    }

    setVisible(show) {
      const changed = this.hidden === show;
      if (changed) {
        this.hidden = !show;
        this.inert = !show;
        this.setAttribute('aria-hidden', String(!show));
      }
      const height = show ? this.offsetHeight : 0;
      if (this.lastHeight !== height) {
        this.lastHeight = height;
        document.documentElement.style.setProperty('--mobile-sticky-bar-height', `${height}px`);
      }
      if (changed) window.dispatchEvent(new Event('kp:mobile-navigation-change'));
    }

    disconnectedCallback() {
      if (this.frame != null) cancelAnimationFrame(this.frame);
      this.frame = null;
      this.headerObserver?.disconnect();
      this.stateObserver?.disconnect();
      this.dialogObserver?.disconnect();
      this.sizeObserver?.disconnect();
      this.headerTransition?.removeEventListener('transitionend', this.schedule);
      this.mobileQuery?.removeEventListener('change', this.schedule);
      window.removeEventListener('scroll', this.schedule);
      window.removeEventListener('resize', this.schedule);
      window.removeEventListener('pageshow', this.schedule);
      window.visualViewport?.removeEventListener('resize', this.schedule);
      window.visualViewport?.removeEventListener('scroll', this.schedule);
      document.removeEventListener('focusin', this.schedule);
      document.removeEventListener('focusout', this.schedule);
      this.hidden = true;
      this.inert = true;
      this.setAttribute('aria-hidden', 'true');
      this.lastHeight = null;
      document.documentElement.style.setProperty('--mobile-sticky-bar-height', '0px');
      window.dispatchEvent(new Event('kp:mobile-navigation-change'));
    }
  });
}
